import { useLoader } from './useLoader';
import { useToast } from '../toasts/useToast';

export const LoaderExample = () => {
  const { showLoader, hideLoader, withLoader } = useLoader();
  const { addNotification } = useToast();

  // Pattern A: Manual Control
  const handleManualLoad = async () => {
    showLoader('Processing manual task...');

    try {
      // Simulate an async operation
      await new Promise((resolve) => setTimeout(resolve, 2000));

      addNotification({
        type: 'success',
        title: 'Manual Task Complete',
        message: 'The manual loading task finished successfully.',
        duration: 3000,
      });
    } catch (err) {
      console.error(err);
      addNotification({
        type: 'error',
        title: 'Manual Task Failed',
        message: 'Something went wrong during manual loading.',
        duration: 5000,
      });
    } finally {
      hideLoader();
    }
  };

  // Pattern B: withLoader Helper (Cleanest)
  const handleWithLoader = async () => {
    try {
      await withLoader(async () => {
        // Simulate an async operation
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return 'Success!';
      }, 'Processing with withLoader...');

      addNotification({
        type: 'success',
        title: 'withLoader Task Complete',
        message: 'The task wrapped in withLoader finished successfully.',
        duration: 3000,
      });
    } catch (err) {
      console.error(err);
      addNotification({
        type: 'error',
        title: 'withLoader Task Failed',
        message: 'Something went wrong during withLoader.',
        duration: 5000,
      });
    }
  };

  // Pattern C: Long-running task
  const handleLongLoad = async () => {
    showLoader('Processing long-running task...');

    try {
      // Simulate a longer async operation
      await new Promise((resolve) => setTimeout(resolve, 4000));

      addNotification({
        type: 'success',
        title: 'Long Task Complete',
        message: 'The long-running task finished successfully.',
        duration: 3000,
      });
    } catch (err) {
      console.error(err);
      addNotification({
        type: 'error',
        title: 'Long Task Failed',
        message: 'Something went wrong during long loading.',
        duration: 5000,
      });
    } finally {
      hideLoader();
    }
  };

  // Pattern D: Error simulation
  const handleError = async () => {
    showLoader('Processing task that will fail...');

    try {
      // Simulate an error
      await new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Simulated error')), 1500)
      );

      addNotification({
        type: 'success',
        title: 'Task Complete',
        message: 'This should not appear.',
        duration: 3000,
      });
    } catch (err) {
      console.error(err);
      addNotification({
        type: 'error',
        title: 'Task Failed',
        message: 'Simulated error occurred as expected.',
        duration: 5000,
      });
    } finally {
      hideLoader();
    }
  };

  // Pattern E: Without message
  const handleNoMessage = async () => {
    showLoader();

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      addNotification({
        type: 'success',
        title: 'No Message Task Complete',
        message: 'Loader shown without custom message.',
        duration: 3000,
      });
    } catch (err) {
      console.error(err);
      addNotification({
        type: 'error',
        title: 'Task Failed',
        message: 'Something went wrong.',
        duration: 5000,
      });
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-white mb-4">Loader System Examples</h3>
      <p className="text-gray-400 mb-6">
        Click the buttons below to see different loader patterns in action. The loader uses React
        Context for state management.
      </p>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleManualLoad}
          className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
        >
          Manual Control (2s)
        </button>
        <button
          onClick={handleWithLoader}
          className="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
        >
          withLoader Helper (1.5s)
        </button>
        <button
          onClick={handleLongLoad}
          className="px-4 py-2 text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors"
        >
          Long Task (4s)
        </button>
        <button
          onClick={handleError}
          className="px-4 py-2 text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
        >
          Error Simulation
        </button>
        <button
          onClick={handleNoMessage}
          className="px-4 py-2 text-white bg-gray-600 rounded hover:bg-gray-700 transition-colors"
        >
          No Message (2s)
        </button>
      </div>

      <div className="mt-6 p-4 bg-gray-700 rounded-lg border border-gray-600">
        <h4 className="text-white font-semibold mb-2">Usage Tips:</h4>
        <ul className="text-gray-300 text-sm space-y-1 list-disc list-inside">
          <li>
            <strong>Manual Control:</strong> Use showLoader/hideLoader when you need fine-grained
            control
          </li>
          <li>
            <strong>withLoader:</strong> Use the helper for automatic cleanup (recommended)
          </li>
          <li>
            <strong>Message:</strong> Optional - provides context about what is happening
          </li>
          <li>
            <strong>Z-Index:</strong> Loader (70) sits above Toasts (60) and Modals (50)
          </li>
        </ul>
      </div>
    </div>
  );
};
